import api from '../axios';
import { handleApiError } from '../errorHandler';

import type {
  CustomerEngagement,
  PaginatedActivityEventsResponse,
  PaginatedCustomerEngagementResponse,
  PostActivityEventsRequest,
  PostActivityEventsResponse,
  QueryActivityEventsParams,
  QueryCustomerEngagementParams,
} from './types';

const ACTIVITY_EVENTS_URL = '/activity/events';
const CUSTOMERS_ENGAGEMENT_URL = '/customers/engagement';
const CONSULTANT_CUSTOMERS_ENGAGEMENT_URL = '/consultants/me/customers/engagement';

export const postActivityEvents = async (
  payload: PostActivityEventsRequest
): Promise<PostActivityEventsResponse> => {
  try {
    const response = await api.post<PostActivityEventsResponse>(ACTIVITY_EVENTS_URL, payload, {
      skipAuthRefresh: true,
      validateStatus: (status) => status === 202 || status === 429,
    });

    if (response.status === 429) {
      const err = new Error('Activity rate limited') as Error & {
        response?: { status: number; data: PostActivityEventsResponse };
      };
      err.response = { status: 429, data: response.data };
      throw err;
    }

    return response.data;
  } catch (error) {
    if ((error as { response?: { status?: number } })?.response?.status === 429) {
      throw error;
    }
    console.error('Erro ao enviar eventos de atividade:', error);
    throw handleApiError(error);
  }
};

export const getActivityEvents = async (
  params?: QueryActivityEventsParams
): Promise<PaginatedActivityEventsResponse> => {
  try {
    const response = await api.get<PaginatedActivityEventsResponse>(ACTIVITY_EVENTS_URL, {
      params,
    });
    return response.data;
  } catch (error) {
    console.error('Erro ao listar eventos de atividade:', error);
    throw handleApiError(error);
  }
};

export const getCustomerActivity = async (
  customerId: string,
  params?: Omit<QueryActivityEventsParams, 'customerId'>
): Promise<PaginatedActivityEventsResponse> => {
  try {
    const response = await api.get<PaginatedActivityEventsResponse>(
      `/customers/${customerId}/activity`,
      { params }
    );
    return response.data;
  } catch (error) {
    console.error('Erro ao listar atividade do cliente:', error);
    throw handleApiError(error);
  }
};

export const getCustomerEngagement = async (customerId: string): Promise<CustomerEngagement> => {
  try {
    const response = await api.get<CustomerEngagement>(`/customers/${customerId}/engagement`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar engajamento do cliente:', error);
    throw handleApiError(error);
  }
};

export const getCustomersEngagement = async (
  params?: QueryCustomerEngagementParams
): Promise<PaginatedCustomerEngagementResponse> => {
  try {
    const response = await api.get<PaginatedCustomerEngagementResponse>(CUSTOMERS_ENGAGEMENT_URL, {
      params,
    });
    return response.data;
  } catch (error) {
    console.error('Erro ao listar engajamento de clientes:', error);
    throw handleApiError(error);
  }
};

export const getConsultantCustomersEngagement = async (
  params?: QueryCustomerEngagementParams
): Promise<PaginatedCustomerEngagementResponse> => {
  try {
    const response = await api.get<PaginatedCustomerEngagementResponse>(
      CONSULTANT_CUSTOMERS_ENGAGEMENT_URL,
      { params }
    );
    return response.data;
  } catch (error) {
    console.error('Erro ao listar engajamento da carteira do consultor:', error);
    throw handleApiError(error);
  }
};
